import { createMachine, transition, type AnyMachineSnapshot } from "xstate";
import {
  ALUMNI,
  AUTOMATOR_ACTIONS_BY_LEVEL,
  AUTOMATOR_COST_GROWTH,
  METERS,
  OFFICE,
  TIERS,
  upgradeCost,
  type CareTierId,
} from "@/data/tunables";
import { UPGRADES, upgradeCostAt, upgradeCurrency, upgradeDef, upgradeLevel, type UpgradeId } from "@/data/upgrades";
import { criminalUpgradeCostAt, criminalUpgradeDef, type CriminalUpgradeId } from "@/data/criminal";
import type { GameState } from "@/state/GameState";

/**
 * M2 — the Ladder machine (v0.10 RFC §3). ONE shape for every leveled
 * purchase in the economy:
 *
 *   level_0 ──UPGRADE[affordable && gateOpen]──▶ level_1 ─… ─▶ level_max
 *
 * A factory builds one machine per distinct maxLevel (memoized), so each
 * instance's chart shows exactly its real rungs. PURE per the Control Room
 * contract: affordability/gating are computed by the caller from plain
 * GameState and ride ON the event; the machine arbitrates and DESCRIBES
 * the purchase — the engine applies the spend + level write.
 *
 * Instances (numbers stay in tunables/upgrades/criminal — never copied):
 * automators ×5 tiers · recruiter · alumni coordinator · PR Firm ·
 * Compliance Officer (+ free SETTLE advance) · prestige shop ×7 ·
 * criminal ladder ×18.
 */

export type LadderEvent =
  | { type: "UPGRADE"; affordable: boolean; gateOpen: boolean }
  | { type: "SETTLE" }; // Compliance only: the Death Letter's free advance

const machineCache = new Map<number, ReturnType<typeof buildLadderMachine>>();

function buildLadderMachine(maxLevel: number) {
  const states: Record<string, object> = {};
  for (let n = 0; n <= maxLevel; n++) {
    const here = `level_${n}`;
    if (n === maxLevel) {
      states[here] = { type: "final" };
    } else {
      states[here] = {
        on: {
          UPGRADE: {
            target: `level_${n + 1}`,
            guard: ({ event }: { event: LadderEvent }) =>
              event.type === "UPGRADE" && event.affordable && event.gateOpen,
            actions: [{ type: "purchase" }],
          },
          // Compliance is the ONLY live SETTLE sender (advanceCompliance);
          // other instances carry the transition unused (shared machine).
          SETTLE: {
            // Free advance — no guards; only the Compliance instance ever
            // sends it (mailbox SETTLE choice).
            target: `level_${n + 1}`,
            actions: [{ type: "advance" }],
          },
        },
      };
    }
  }
  return createMachine({
    types: {} as { events: LadderEvent },
    id: `ladder_${maxLevel}`,
    initial: "level_0",
    states,
  });
}

export function ladderMachine(maxLevel: number) {
  let m = machineCache.get(maxLevel);
  if (!m) {
    if (!Number.isInteger(maxLevel) || maxLevel < 1) {
      throw new Error(`ladderMachine: maxLevel must be a positive integer (got ${maxLevel})`);
    }
    m = buildLadderMachine(maxLevel);
    machineCache.set(maxLevel, m);
  }
  return m;
}

/** One leveled purchase, described: where its level lives, what the next
 * rung costs, what pays for it, and any gate that must be open. */
export interface LadderInstance {
  id: string;
  maxLevel: number;
  level: (s: GameState) => number;
  cost: (s: GameState) => number;
  balance: (s: GameState) => number;
  /** null = open; a string = the fail-loud reason the gate is shut. */
  gate?: (s: GameState) => string | null;
}

export function projectLadder(inst: LadderInstance, s: GameState): AnyMachineSnapshot {
  const lvl = inst.level(s);
  if (lvl < 0 || lvl > inst.maxLevel) {
    throw new Error(`projectLadder: ${inst.id} level ${lvl} outside 0..${inst.maxLevel}`);
  }
  return ladderMachine(inst.maxLevel).resolveState({ value: `level_${lvl}` }) as AnyMachineSnapshot;
}

export type LadderFire =
  | { allowed: true; cost: number; newLevel: number }
  | { allowed: false; reason: "maxed" | "funds" | "gated"; cost: number; gateMessage?: string };

/** Ask the machine whether UPGRADE may fire. Pure — the caller spends. */
export function fireLadderUpgrade(inst: LadderInstance, s: GameState): LadderFire {
  const lvl = inst.level(s);
  const cost = lvl >= inst.maxLevel ? 0 : inst.cost(s);
  if (lvl >= inst.maxLevel) return { allowed: false, reason: "maxed", cost };
  const gateMessage = inst.gate?.(s) ?? null;
  const before = projectLadder(inst, s);
  const [after, actions] = transition(ladderMachine(inst.maxLevel), before, {
    type: "UPGRADE",
    affordable: inst.balance(s) >= cost,
    gateOpen: gateMessage === null,
  });
  const purchased = actions.some((a) => (a as { type?: string }).type === "purchase");
  if (purchased && after.value === `level_${lvl + 1}`) {
    return { allowed: true, cost, newLevel: lvl + 1 };
  }
  if (gateMessage !== null) return { allowed: false, reason: "gated", cost, gateMessage };
  return { allowed: false, reason: "funds", cost };
}

/** The Compliance Officer's free SETTLE advance (Death Letter choice).
 * Pure — returns the new level, or null when the ladder is maxed (the
 * final state has no transitions; the machine refuses). */
export function fireLadderSettle(inst: LadderInstance, s: GameState): number | null {
  const lvl = inst.level(s);
  if (lvl >= inst.maxLevel) return null;
  const [after, actions] = transition(ladderMachine(inst.maxLevel), projectLadder(inst, s), { type: "SETTLE" });
  const advanced = actions.some((a) => (a as { type?: string }).type === "advance");
  return advanced && after.value === `level_${lvl + 1}` ? lvl + 1 : null;
}

/* ── the instance registry (structure here; numbers stay at their source) ── */

export const LADDER = {
  automator: (tier: CareTierId): LadderInstance => ({
    id: `automator:${tier}`,
    maxLevel: AUTOMATOR_ACTIONS_BY_LEVEL.length - 1,
    level: (s) => s.run.tiers[tier].automatorLevel,
    cost: (s) => upgradeCost(TIERS[tier].automatorBaseCost, AUTOMATOR_COST_GROWTH, s.run.tiers[tier].automatorLevel),
    balance: (s) => s.run.funding,
  }),
  recruiter: (): LadderInstance => ({
    id: "recruiter",
    maxLevel: OFFICE.recruiterIntervalByLevel.length - 1,
    level: (s) => s.run.office.recruiterLevel,
    cost: (s) => upgradeCost(OFFICE.recruiterBaseCost, OFFICE.recruiterCostGrowth, s.run.office.recruiterLevel),
    balance: (s) => s.run.funding,
  }),
  coordinator: (): LadderInstance => ({
    id: "coordinator",
    maxLevel: AUTOMATOR_ACTIONS_BY_LEVEL.length - 1,
    level: (s) => s.run.alumni.coordinatorLevel,
    cost: (s) => upgradeCost(ALUMNI.coordinatorBaseCost, AUTOMATOR_COST_GROWTH, s.run.alumni.coordinatorLevel),
    balance: (s) => s.run.funding,
    gate: (s) => (s.run.availableTiers.includes("alumni") ? null : "the Alumni Network is not open yet"),
  }),
  prFirm: (): LadderInstance => ({
    id: "prFirm",
    maxLevel: METERS.mitigationMaxLevel,
    level: (s) => s.run.prFirmLevel,
    cost: (s) => upgradeCost(METERS.prFirmBaseCost, METERS.mitigationCostGrowth, s.run.prFirmLevel),
    balance: (s) => s.run.funding,
  }),
  compliance: (): LadderInstance => ({
    id: "compliance",
    maxLevel: METERS.mitigationMaxLevel,
    level: (s) => s.run.complianceOfficerLevel,
    cost: (s) => upgradeCost(METERS.complianceBaseCost, METERS.mitigationCostGrowth, s.run.complianceOfficerLevel),
    balance: (s) => s.run.funding,
  }),
  fixer: (): LadderInstance => ({
    id: "fixer",
    maxLevel: METERS.mitigationMaxLevel,
    level: (s) => s.run.fixerLevel,
    cost: (s) => upgradeCost(METERS.fixerBaseCost, METERS.mitigationCostGrowth, s.run.fixerLevel),
    balance: (s) => s.run.funding,
  }),
  shop: (id: UpgradeId): LadderInstance => {
    const def = upgradeDef(id);
    return {
      id: `shop:${id}`,
      maxLevel: def.maxLevel,
      level: (s) => upgradeLevel(s.account, id),
      cost: (s) => upgradeCostAt(s.account, id),
      balance: (s) =>
        upgradeCurrency(def) === "streetKnowledge" ? s.account.streetKnowledge : s.account.reputationCapital,
      gate: (s) => (s.run.prestigeOffered ? null : "the shop is only open during a prestige offer"),
    };
  },
  criminal: (id: CriminalUpgradeId): LadderInstance => {
    const def = criminalUpgradeDef(id);
    return {
      id: `criminal:${id}`,
      maxLevel: def.maxLevel,
      level: (s) => s.run.criminal.upgrades[id] ?? 0,
      cost: (s) => criminalUpgradeCostAt(def, s.run.criminal.upgrades[id] ?? 0),
      balance: (s) => s.run.funding,
      gate: (s) => {
        if (!s.run.criminal.unlocked) return "the criminal track is not unlocked yet";
        if (def.tier > s.run.criminal.tierAccess) {
          return `tier ${def.tier} is locked (access up to ${s.run.criminal.tierAccess})`;
        }
        return null;
      },
    };
  },
} as const;

/** Every BASE ladder instance (automators/recruiter/coordinator/mitigation/
 * shop). Criminal ladders are per-upgrade-id — consumers append them via
 * `CRIMINAL_UPGRADES.map((u) => LADDER.criminal(u.id))` (see controlRoom
 * feeds() and genMachineDocs), so they are NOT in this list. (v0.10 review:
 * the old docstring claimed completeness and misled a reviewer.) */
export function allLadderInstances(): LadderInstance[] {
  const tiers = Object.keys(TIERS) as CareTierId[];
  return [
    ...tiers.map((t) => LADDER.automator(t)),
    LADDER.recruiter(),
    LADDER.coordinator(),
    LADDER.prFirm(),
    LADDER.compliance(),
    LADDER.fixer(),
    ...UPGRADES.map((u) => LADDER.shop(u.id)),
  ];
}
